import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { Link } from 'react-router-dom';

import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';

import Message from 'components/Message';

import messages from './WeeklyEarnings.messages';
import './WeeklyEarningsTooltip.scss';

const { block, element } = bem('weekly-earnings-tooltip');

export const CTA_DESTINATION = Object.freeze({
  CAMPAIGN: 'campaign',
  OPPORTUNITIES: 'opportunities',
});

export const TOOLTIP_VARIANT = Object.freeze({
  EARNINGS: 'earnings',
  NO_SPOTS: 'no_spots',
  NO_CP_BOOKINGS: 'no_cp_bookings',
});

export const tooltipShape = PropTypes.shape({
  title: PropTypes.string.isRequired,
  periodSubtitle: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(TOOLTIP_VARIANT)),
  stats: PropTypes.arrayOf(PropTypes.shape({})),
  ctaDestination: PropTypes.string,
});

export const resolveCta = (excessSpotsFilledPercent, classpassBooked) => {
  const pct = Number(excessSpotsFilledPercent) || 0;
  const bookings = Number(classpassBooked) || 0;

  if (pct >= 31) {
    return bookings >= 4 ? CTA_DESTINATION.OPPORTUNITIES : CTA_DESTINATION.CAMPAIGN;
  }
  if (pct >= 11) {
    return bookings >= 16 ? CTA_DESTINATION.OPPORTUNITIES : CTA_DESTINATION.CAMPAIGN;
  }
  return CTA_DESTINATION.CAMPAIGN;
};

function TooltipCallout({ calloutMessage, values }) {
  return (
    <div className={element('callout')}>
      <Isvg aria-hidden className={element('callout-icon')} src={ICONS.infoBrand} />
      <p className={element('callout-text')}>
        <Message message={calloutMessage} values={values} />
      </p>
    </div>
  );
}
TooltipCallout.propTypes = {
  calloutMessage: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  values: PropTypes.shape({}),
};

function WeeklyEarningsTooltip({ tooltip, campaignsPath, opportunitiesPath, hideTitle, venueId }) {
  const { title, stats, ctaDestination, variant } = tooltip;

  const trackCta = type => {
    Segment.event('Click SmartRate Hover CTA', { type, venue_id: venueId });
  };

  const renderCallout = () => {
    if (variant === TOOLTIP_VARIANT.NO_SPOTS) {
      return (
        <TooltipCallout
          calloutMessage={messages.tooltipNoSpotsCallout}
          values={{
            a: text => (
              <Link className={element('link')} onClick={() => trackCta('Opportunities')} to={opportunitiesPath}>
                {text}
              </Link>
            ),
          }}
        />
      );
    }

    if (variant === TOOLTIP_VARIANT.NO_CP_BOOKINGS) {
      return (
        <TooltipCallout
          calloutMessage={messages.tooltipNoCpBookingsCallout}
          values={{
            a: text => (
              <Link className={element('link')} onClick={() => trackCta('Campaigns')} to={campaignsPath}>
                {text}
              </Link>
            ),
          }}
        />
      );
    }

    if (ctaDestination === CTA_DESTINATION.CAMPAIGN) {
      return (
        <TooltipCallout
          calloutMessage={messages.tooltipCalloutCampaign}
          values={{
            a: text => (
              <Link className={element('link')} onClick={() => trackCta('Campaigns')} to={campaignsPath}>
                {text}
              </Link>
            ),
          }}
        />
      );
    }

    if (ctaDestination === CTA_DESTINATION.OPPORTUNITIES) {
      return (
        <TooltipCallout
          calloutMessage={messages.tooltipCalloutOpportunities}
          values={{
            a: text => (
              <Link className={element('link')} onClick={() => trackCta('Opportunities')} to={opportunitiesPath}>
                {text}
              </Link>
            ),
          }}
        />
      );
    }

    return null;
  };

  return (
    <div className={block()}>
      {!hideTitle && <p className={element('title')}>{title}</p>}
      {stats && stats.length > 0 && (
        <div className={element('stats')}>
          {stats.map(({ key, label, value }) => (
            <div className={element('stat')} key={key}>
              <span className={element('stat-label')}>
                <Message message={label.message} values={label.values} />
              </span>
              <span className={element('stat-value')}>{value}</span>
            </div>
          ))}
        </div>
      )}
      {renderCallout()}
    </div>
  );
}

WeeklyEarningsTooltip.propTypes = {
  hideTitle: PropTypes.bool,
  venueId: PropTypes.number,
  tooltip: PropTypes.shape({
    title: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(Object.values(TOOLTIP_VARIANT)),
    stats: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.shape({
          message: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
          values: PropTypes.shape({}),
        }).isRequired,
        value: PropTypes.node.isRequired,
      })
    ),
    ctaDestination: PropTypes.oneOf(Object.values(CTA_DESTINATION)),
  }).isRequired,
  campaignsPath: PropTypes.string.isRequired,
  opportunitiesPath: PropTypes.string.isRequired,
};

export default WeeklyEarningsTooltip;
